import { useCallback, useEffect, useRef, useState } from 'react';
import { Question } from './useConversationalOnboarding.ts';

interface UseSummaryModeOptions {
  currentQuestion: Question | null;
  onExitComplete: () => void;
}

export const useSummaryMode = ({ currentQuestion, onExitComplete }: UseSummaryModeOptions) => {
  const [summaryModeActive, setSummaryModeActive] = useState(false);
  const pendingSummaryModeRef = useRef(false);
  const prevQuestionIdRef = useRef<string | null>(null);
  const wasSummaryRef = useRef(false);
  const isSummaryQuestion = currentQuestion?.type === 'interactive' && currentQuestion?.interactiveType === 'summary';

  useEffect(() => {
    const prevId = prevQuestionIdRef.current;
    const nextId = currentQuestion?.id ?? null;

    if (isSummaryQuestion) {
      if (prevId && prevId !== nextId) {
        pendingSummaryModeRef.current = true;
        setSummaryModeActive(false);
      } else {
        pendingSummaryModeRef.current = false;
        setSummaryModeActive(true);
      }
    } else if (wasSummaryRef.current) {
      pendingSummaryModeRef.current = true;
      setSummaryModeActive(true);
    } else {
      pendingSummaryModeRef.current = false;
      setSummaryModeActive(false);
    }

    prevQuestionIdRef.current = nextId;
    wasSummaryRef.current = isSummaryQuestion;
  }, [isSummaryQuestion, currentQuestion?.id]);

  const handleSummaryExitComplete = useCallback(() => {
    onExitComplete();
    if (pendingSummaryModeRef.current) {
      pendingSummaryModeRef.current = false;
      setSummaryModeActive(isSummaryQuestion);
    }
  }, [onExitComplete, isSummaryQuestion]);

  return {
    summaryModeActive,
    isSummaryQuestion,
    handleSummaryExitComplete,
  };
};
